import { createContext, useContext } from 'react';
import {
    gridColumnDefinitionsSelector,
    gridColumnVisibilityModelSelector,
    gridFilteredSortedRowIdsSelector,
    gridRowTreeSelector,
    gridSortedRowIdsSelector,
    type GridApiPro,
    type GridRowId
} from '../lib/dataGrid';
import { messagesByLocale } from '../i18n';
import { composeFileName } from '../utils/downloadFile';

type GridApi = { current: GridApiPro };

export interface CsvColumnChoice {
    field: string;
    label: string;
    /** Shown in the grid right now — ticked when the dialog opens. */
    visible: boolean;
}

export type CsvRowScope = 'filtered' | 'all';

/**
 * The columns a CSV may hold, in the order the grid shows them. The row
 * actions and the checkbox column carry `disableExport`; an actions column a
 * grid built itself is dropped by its type.
 */
export function csvColumnChoices(apiRef: GridApi): CsvColumnChoice[] {
    const visibility = gridColumnVisibilityModelSelector(apiRef);
    return gridColumnDefinitionsSelector(apiRef)
        .filter(
            (column) =>
                column.disableExport !== true && column.type !== 'actions'
        )
        .map((column) => ({
            field: column.field,
            label: column.headerName || column.field,
            visible: visibility[column.field] !== false
        }));
}

/** The column panel's own texts, for the CSV dialog's column picker. */
export function csvPickerTexts(apiRef: GridApi) {
    const text = (
        key:
            | 'columnsManagementSearchTitle'
            | 'columnsManagementNoColumns'
            | 'columnsManagementReset'
    ) => String(apiRef.current.getLocaleText(key));
    return {
        search: text('columnsManagementSearchTitle'),
        noColumns: text('columnsManagementNoColumns'),
        reset: text('columnsManagementReset')
    };
}

/**
 * The rows to write, in the grid's sort order. `filtered` is what the filters
 * and the search leave standing — collapsed tree children included. Tree
 * nodes the grid made up for a missing parent hold no data and stay out.
 */
export function csvRowIds(apiRef: GridApi, scope: CsvRowScope): GridRowId[] {
    const tree = gridRowTreeSelector(apiRef);
    const ids =
        scope === 'filtered'
            ? gridFilteredSortedRowIdsSelector(apiRef)
            : gridSortedRowIdsSelector(apiRef);
    return ids.filter((id) => {
        const node = tree[id];
        if (!node) {
            return false;
        }
        if (node.type === 'group') {
            return !node.isAutoGenerated;
        }
        return node.type === 'leaf' || node.type === 'pinnedRow';
    });
}

/**
 * Semicolon, CRLF and a UTF-8 BOM, as in the Zeiterfassung CSV: a German
 * Excel opened by double-click splits on ';' and reads Umlauts only with the
 * BOM. The cells are what the grid shows (its `valueFormatter`s).
 */
export function gridCsvBlob(
    apiRef: GridApi,
    fields: string[],
    scope: CsvRowScope
): Blob {
    const text = apiRef.current.getDataAsCsv({
        fields,
        delimiter: ';',
        includeColumnGroupsHeaders: false,
        getRowsToExport: () => csvRowIds(apiRef, scope)
    });
    return new Blob([`\ufeff${text}\r\n`], {
        type: 'text/csv;charset=utf-8'
    });
}

/**
 * Where the user is, as the parts of an export's file name: the open
 * Maßnahme's number and the menu path down to the page — ["M-2024-0123",
 * "Funde"], ["Auswertung", "Befundbeschreibungen"]. Set by the office layout.
 */
export const ExportLocationContext = createContext<readonly string[]>([]);

/**
 * A text for a file name, in German whatever the UI language: the file is
 * filed away and opened elsewhere (see `TimeEntryVariant.exportFileStem`).
 */
export function germanText(messageId: string): string {
    return messagesByLocale.de[messageId] ?? messageId;
}

/**
 * The location, then what this grid holds when the page alone does not say
 * (a Nachweis' name, one of several grids on a page):
 * "2026_10_16-13_55-M-2024-0123_Funde.csv".
 */
export function gridCsvFileName(
    location: readonly string[],
    exportName?: string
): string {
    const parts = [...location, exportName].filter((part): part is string =>
        Boolean(part)
    );
    return composeFileName(parts.length ? parts : ['Tabelle'], '.csv');
}

/**
 * Lets an export menu inside the toolbar offer the CSV export as one of its
 * entries — the toolbar then drops its own button, so a grid shows one
 * download icon, not two.
 */
export interface GridCsvExportHandle {
    open: () => void;
    /** Called by a menu while mounted; returns its unregister. */
    register: () => () => void;
}

export const GridCsvExportContext = createContext<GridCsvExportHandle | null>(
    null
);

export function useGridCsvExport(): GridCsvExportHandle | null {
    return useContext(GridCsvExportContext);
}
